import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { terminalsStore } from "../stores/terminalsStore";
import { workbenchesStore } from "../stores/workbenchesStore";
import type { WorkbenchContainer } from "../types";

const CONFIRM_DELETE_WINDOW_MS = 3000;
const ERROR_DISMISS_MS = 4000;

/**
 * Vertical workspace (workbench) switcher for the activity-bar rail. Same
 * switch/create/rename/delete behaviour as the horizontal WorkbenchTabs, but
 * laid out for the rail so the feature is reachable from EVERY surface — most
 * importantly the terminal pane, which previously had no way to change
 * workspaces. Reads the stores directly (like WorkbenchTabs) so it needs no
 * prop threading through SessionRail.
 */
export function WorkbenchRailPanel() {
  const { items: workbenches, activeId } = workbenchesStore.useStore();
  const terminals = terminalsStore.useStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  // Single commit path: onBlur is the ONLY place a rename saves. Enter and
  // Escape both blur the input (rather than saving/cancelling inline), so the
  // commit runs exactly once while the element is still focused — the
  // well-known blur-on-unmount can't then fire a second, stale save. Escape
  // sets cancelEditRef so the shared blur path discards the draft. Mirrors
  // TerminalTile's rename in TerminalPane.tsx.
  const cancelEditRef = useRef(false);
  // Two-click delete, matching WorkbenchTabs: first click arms, second within
  // the window confirms.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!error) return;
    if (errorTimer.current) clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setError(null), ERROR_DISMISS_MS);
    return () => {
      if (errorTimer.current) clearTimeout(errorTimer.current);
    };
  }, [error]);

  useEffect(
    () => () => {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
    },
    []
  );

  const countFor = (id: string) =>
    terminals.terminals.filter((terminal) => (terminal.workbenchId || "wb-default") === id).length;

  async function addWorkbench() {
    try {
      const created = await api.createWorkbench(`Workbench ${workbenches.length + 1}`);
      workbenchesStore.setState({
        items: [...workbenches, created],
        activeId: created.id,
        loading: false,
        error: null,
      });
      setEditingId(created.id);
      setEditingName(created.name);
      setError(null);
    } catch (addError) {
      setError(addError instanceof Error ? addError.message : "Could not create workspace");
    }
  }

  async function commitRename(id: string) {
    if (cancelEditRef.current) {
      // Escape routed through onBlur — discard the draft, don't save.
      cancelEditRef.current = false;
      setEditingId(null);
      return;
    }
    const name = editingName.trim();
    if (!name) {
      setEditingId(null);
      return;
    }
    try {
      const updated = await api.updateWorkbench(id, { name });
      workbenchesStore.setState((current) => ({
        ...current,
        items: current.items.map((item) => (item.id === id ? updated : item)),
        error: null,
      }));
      setEditingId(null);
      setError(null);
    } catch (renameError) {
      setError(renameError instanceof Error ? renameError.message : "Could not rename workspace");
    }
  }

  function armDelete(workbench: WorkbenchContainer) {
    if (confirmingId === workbench.id) {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      void performDelete(workbench);
      return;
    }
    setConfirmingId(workbench.id);
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    confirmTimer.current = setTimeout(() => setConfirmingId(null), CONFIRM_DELETE_WINDOW_MS);
  }

  async function performDelete(workbench: WorkbenchContainer) {
    if (workbenches.length <= 1) {
      setError("Cannot delete the last workspace.");
      setConfirmingId(null);
      return;
    }
    const inWorkbench = terminals.terminals.filter(
      (terminal) => (terminal.workbenchId || "wb-default") === workbench.id
    );
    await Promise.all(
      inWorkbench.map((terminal) => api.deleteTerminal(terminal.id).catch(() => undefined))
    );
    try {
      await api.deleteWorkbench(workbench.id);
      workbenchesStore.setState((current) => {
        const items = current.items.filter((item) => item.id !== workbench.id);
        return {
          ...current,
          items,
          activeId:
            current.activeId === workbench.id ? items[0]?.id || "wb-default" : current.activeId,
          error: null,
        };
      });
      terminalsStore.setState((current) => ({
        ...current,
        terminals: current.terminals.filter(
          (terminal) => (terminal.workbenchId || "wb-default") !== workbench.id
        ),
      }));
      setConfirmingId(null);
      setError(null);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Could not delete workspace");
      setConfirmingId(null);
    }
  }

  return (
    <div className="rail-section">
      <div className="workbench-rail__list" role="listbox" aria-label="Workspaces">
        {workbenches.map((workbench) => {
          const count = countFor(workbench.id);
          const active = workbench.id === activeId;
          return editingId === workbench.id ? (
            <form
              key={workbench.id}
              className="workbench-rail__row workbench-rail__row--editing"
              onSubmit={(event) => event.preventDefault()}
            >
              <input
                className="rail-input rail-input--text"
                value={editingName}
                onChange={(event) => setEditingName(event.target.value)}
                onBlur={() => void commitRename(workbench.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    cancelEditRef.current = true;
                    event.currentTarget.blur();
                  }
                }}
                autoFocus
              />
            </form>
          ) : (
            <div
              key={workbench.id}
              role="option"
              aria-selected={active}
              tabIndex={0}
              className={`workbench-rail__row ${active ? "is-active" : ""}`}
              onClick={() => workbenchesStore.setState({ activeId: workbench.id })}
              onDoubleClick={() => {
                setEditingId(workbench.id);
                setEditingName(workbench.name);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  workbenchesStore.setState({ activeId: workbench.id });
                }
              }}
              title={workbench.name}
            >
              <span className="workbench-rail__emoji" aria-hidden="true">
                {workbench.emoji || workbench.name.trim().charAt(0).toUpperCase() || "#"}
              </span>
              <span className="workbench-rail__name">{workbench.name}</span>
              {count > 0 ? (
                <span className="workbench-rail__count" title={`${count} terminal(s)`}>
                  {count}
                </span>
              ) : null}
              {workbenches.length > 1 ? (
                <button
                  type="button"
                  className={`workbench-rail__delete ${confirmingId === workbench.id ? "is-confirming" : ""}`}
                  title={
                    confirmingId === workbench.id
                      ? "Click again to confirm"
                      : "Delete workspace (click twice)"
                  }
                  aria-label={`Delete ${workbench.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    armDelete(workbench);
                  }}
                >
                  {confirmingId === workbench.id ? "Confirm" : "×"}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="workbench-rail__add"
        onClick={() => void addWorkbench()}
        title="Create a new workspace"
      >
        <Plus size={15} />
        <span>New workspace</span>
      </button>
      {error ? <p className="route-error">{error}</p> : null}
    </div>
  );
}
